/**
 * View component for /user/login
 *
 * On render cycle this component checks to see if the redirectToReferrer boolean
 * is true (flipped on successful login).  If true, send the user back to the
 * referring page.  If false, show user login form.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */

// import primary libraries
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Redirect from 'react-router-dom/Redirect'
import withRouter from 'react-router-dom/withRouter'
import axios from 'axios'

// import actions actions
import * as firmActions from '../../firm/firmActions.js'
import * as userActions from '../userActions.js'
import * as activityActions from '../../activity/activityActions.js'

// import form components
import AlertModal from '../../../global/components/modals/AlertModal.jsx'
import Binder from '../../../global/components/Binder.jsx'
import brandingName from '../../../global/enum/brandingName.jsx'

// import user components
import UserLayout from '../components/UserLayout.jsx'
import UserLoginForm from '../components/UserLoginForm.jsx'
import UserTermsServicesModal from '../components/UserTermsServicesModal.jsx'
import UserQRCodeModal from '../components/UserQRCodeModal.jsx'

// UI Switcher
import UISwitchChecker from '../../../global/practice/components/PracticeUISwitcher.jsx'

import { Helmet } from 'react-helmet-async'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'

class UserLogin extends Binder {
  constructor(props) {
    super(props)
    this.state = {
      errorMessage: '',
      errorCode: '',
      isErrorModalOpen: false,
      magicLinkSent: false,
      redirectToReferrer: false,
      user: {
        username: '',
        password: '',
        emailError: null,
      },
      userTermsServiceModalOpen: false,
      msalInstance: null,
      userQRCodeModalOpen: false,
      qrCodeUrl: '',
      tempSecret: '',
      tempUser: {},
      passwordResetToken: '',
    }
    this._bind(
      '_handleFormChange',
      '_handleFormSubmit',
      '_goToResetPass',
      '_sendMagicLink',
      '_nextToLogin',
      '_handleVerifyToken',
    )
  }

  componentDidMount() {
    const { dispatch } = this.props
    localStorage.clear()
    dispatch(firmActions.fetchSingleFirmByDomain())
  }

  _handleFormChange(e) {
    var nextState = this.state.user
    nextState[e.target.name] = e.target.value
    this.setState(nextState)
  }

  _nextToLogin() {
    const { history } = this.props

    history.push('/user/reset-password/' + this.state.passwordResetToken)
  }

  _handleFormSubmit(e) {
    e.preventDefault()
    const { dispatch, history, location } = this.props
    if (this.state.user.emailError) {
      return
    }

    dispatch(
      userActions.sendLogin(this.state.username, this.state.password, 'main'),
    ).then(action => {
      console.log('Im here here here')
      console.log('action action', action)
      if (action.success) {
        dispatch(activityActions.sendCreateLoggedInUserActivity())

        //enable 2fa
        console.log('login action', action)

        if (action.redirect_url) {
          if (action.logout) {
            dispatch(userActions.sendLogout()).then(() => {
              window.location = action.redirect_url
            })
          } else {
            window.location = action.redirect_url
          }
        } else if (action.user.enable_2fa) {
          this.setState({
            qrCodeUrl: action.user.qrcode_data,
            tempSecret: action.user.secret_2fa,
            userQRCodeModalOpen: true,
            tempUser: action.user,
          })
        } else if (action.force_2fa) {
          history.push({
            pathname: '/user/force_2fa',
          })
        } else {
          if (location.state.from) {
            this.setState({ redirectToReferrer: true })
          } else {
            history.push({
              pathname: '/',
              search: window.location.search,
            })
          }
        }
      } else {
        const currUser = action.user ? action.user : {}
        const rawMessage =
          action.message || action.error || 'An error occurred.'
        const errorMessage = rawMessage.replace(
          /^(Internal error|Validation error): /,
          '',
        )
        if (action.error != 'user-first-login') {
          this.setState({
            errorMessage,
            errorCode: action.errorCode || '',
            isErrorModalOpen: true,
          })
        } else {
          this.setState(
            { passwordResetToken: action.passwordResetToken },
            () => {
              if (currUser.isStaff) {
                this.setState({
                  userTermsServiceModalOpen: true,
                })
              } else {
                this._nextToLogin()
              }
            },
          )
        }
      }
    })
  }

  _sendMagicLink() {
    const { dispatch } = this.props
    dispatch(userActions.sendMagicLink(this.state.username)).then(action => {
      if (action.success) {
        this.setState({ magicLinkSent: true })
      } else {
        this.setState({
          errorMessage: action.error,
          isErrorModalOpen: true,
        })
      }
    })
  }

  _goToResetPass() {
    const { history } = this.props
    console.log('go to reset password')
    history.push('/user/forgot-password')
  }

  _handleVerifyToken(token) {
    console.log('this is the token', token)
    console.log('this is the secret', this.state.tempSecret)

    const { history, location } = this.props

    axios({
      method: 'POST',
      url: '/api/users/verify-2fatoken',
      data: {
        user: this.state.tempUser,
        token: token,
      },
    })
      .then(({ data }) => {
        console.log('verify api', data)

        if (data.success) {
          this.setState({ userQRCodeModalOpen: false })
          if (location.state.from) {
            this.setState({ redirectToReferrer: true })
          } else {
            history.push({
              pathname: '/',
              search: window.location.search,
            })
          }
        } else {
          alert('Invalid OTP')
        }
      })
      .catch(err => {
        console.log(err)
        console.log('failed to authorize 2fa')
      })
  }

  render() {
    const { firmStore } = this.props
    const { from } = this.props.location.state || { from: { pathname: '/' } }
    const { redirectToReferrer, user } = this.state

    const selectedFirm = firmStore.selected.getItem()

    const firmEmpty =
      !selectedFirm || !selectedFirm._id || firmStore.selected.didInvalidate

    const firmFetching = firmStore.selected.isFetching

    const logoUrl =
      selectedFirm &&
      selectedFirm._id &&
      selectedFirm.logoUrl &&
      `/api/firms/logo/${selectedFirm._id}/${selectedFirm.logoUrl}`
    const firmLogo = brandingName.image.logoBlack
    const isLockedOut =
      this.state.errorCode === 'ACCOUNT_LOCKED' ||
      this.state.errorCode === 'RATE_LIMITED'

    if (redirectToReferrer) {
      return <Redirect to={from} />
    } else {
      return (
        <UserLayout>
          <UISwitchChecker />
          <Helmet>
            <title>
              Login to {selectedFirm ? selectedFirm.name : brandingName.title}
            </title>
          </Helmet>
          <div className="yt-container">
            <div className="yt-row center-horiz">
              {firmEmpty ? (
                firmFetching ? (
                  <div className="-loading-hero hero">
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                  </div>
                ) : (
                  <UserLoginForm
                    user={user}
                    handleFormSubmit={this._handleFormSubmit}
                    handleFormChange={this._handleFormChange}
                    location={this.props.location}
                    logoUrl={firmLogo}
                    magicLinkSent={this.state.magicLinkSent}
                    sendMagicLink={this._sendMagicLink}
                    selectedFirm={selectedFirm}
                  />
                )
              ) : (
                <UserLoginForm
                  user={user}
                  handleFormSubmit={this._handleFormSubmit}
                  handleFormChange={this._handleFormChange}
                  location={this.props.location}
                  logoUrl={logoUrl || firmLogo}
                  magicLinkSent={this.state.magicLinkSent}
                  sendMagicLink={this._sendMagicLink}
                  showPoweredBy={true}
                  handleMSAuthCallback={this._handleMSAuthCallback}
                  selectedFirm={selectedFirm}
                />
              )}
            </div>
            <Dialog
              open={this.state.isErrorModalOpen}
              onClose={() => this.setState({ isErrorModalOpen: false })}
              PaperProps={{
                sx: { minWidth: 400, p: 1 },
              }}
              sx={{
                alignItems: 'center',
                '& .MuiDialog-container': {
                  alignItems: 'flex-start',
                  paddingTop: '10%',
                },
              }}
            >
              <DialogTitle sx={{ pb: 1, fontSize: '1.5rem' }}>
                Error with sign in
              </DialogTitle>
              <DialogContent sx={{ pb: 2 }}>
                <DialogContentText sx={{ fontSize: '1rem' }}>
                  {this.state.errorMessage}
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                {!isLockedOut && (
                  <Button
                    onClick={this._goToResetPass}
                    color="inherit"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Reset Password
                  </Button>
                )}
                <Button
                  onClick={() => this.setState({ isErrorModalOpen: false })}
                  variant="contained"
                  color="error"
                  sx={{ fontWeight: 'bold' }}
                >
                  {isLockedOut ? 'OK' : 'Try again'}
                </Button>
              </DialogActions>
            </Dialog>
            <UserTermsServicesModal
              close={() => this.setState({ userTermsServiceModalOpen: false })}
              isOpen={this.state.userTermsServiceModalOpen}
              acceptTermsService={this._nextToLogin}
            />
            <UserQRCodeModal
              close={() => this.setState({ userQRCodeModalOpen: false })}
              isOpen={this.state.userQRCodeModalOpen}
              acceptTermsService={() => {}}
              url={this.state.qrCodeUrl}
              handleVerifyToken={this._handleVerifyToken}
            />
          </div>
        </UserLayout>
      )
    }
  }
}

UserLogin.propTypes = {
  dispatch: PropTypes.func.isRequired,
}

const mapStoreToProps = store => {
  return {
    firmStore: store.firm,
  }
}

export default withRouter(connect(mapStoreToProps)(UserLogin))
